import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import { Seo } from "../components/Seo";
import { Eyebrow, Reveal } from "../components/ui-bits";
import { CTABand, HubCard } from "../components/PageSections";
import { HOLIDAYS, SITE } from "../data/site";

export default function Seasonal() {
  return (
    <>
      <Seo
        title="Holiday Flowers Coventry RI | JIF – Jack Iannotti Flowers"
        description="Valentine's roses, Mother's Day bouquets, Thanksgiving centerpieces, Christmas poinsettias — holiday flowers from Coventry's family florist. (401) 822-4440."
      />
      <section className="mx-auto max-w-7xl px-5 pb-16 pt-40 sm:px-8">
        <Reveal>
          <Eyebrow>Holidays & Seasonal</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl lg:text-6xl">
            The Holidays, in Flowers
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-charcoal/85 sm:text-[18px]">
            From Valentine's roses to Christmas poinsettias, the shop marks the year in flowers. These pages stay
            here all year — when the season comes, call early for the best of the cooler.
          </p>
        </Reveal>
      </section>
      <section className="mx-auto max-w-7xl px-5 pb-20 sm:px-8">
        <div className="grid gap-10 sm:grid-cols-2">
          {HOLIDAYS.map((h) => (
            <HubCard
              key={h.path}
              href={h.path}
              image={h.image}
              imageAlt={h.imageAlt}
              eyebrow="Holidays & Seasonal"
              title={h.name}
              blurb={h.lede}
              testid={`holiday-card-${h.slug}`}
            />
          ))}
        </div>
      </section>
      <section className="border-y border-gold/25 bg-white/50" data-testid="wedding-block">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-20 sm:px-8 sm:py-24 lg:grid-cols-2 lg:gap-20">
          <Reveal>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-xl border border-gold/60" aria-hidden="true" />
              <img
                src="/images/bridal-bouquet.webp"
                alt="Bridal bouquet of blush garden roses and greenery by JIF – Jack Iannotti Flowers, Coventry RI"
                loading="lazy"
                className="relative aspect-[4/3] w-full rounded-xl object-cover shadow-xl shadow-plum/15"
              />
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <Eyebrow>Wedding Flowers</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-snug text-plum sm:text-4xl">
              Beautiful Flowers for Your Special Day
            </h2>
            <p className="mt-6 text-[17px] leading-relaxed text-charcoal/85">
              From bridal bouquets to ceremony flowers and reception arrangements, JIF – Jack Iannotti Flowers brings
              a personal touch to your wedding day.
            </p>
            <p className="mt-4 text-[17px] leading-relaxed text-charcoal/85">
              Every wedding is unique, and we'd love to hear about yours. Please call to schedule your personal
              wedding consultation.
            </p>
            <a
              href={SITE.phoneHref}
              data-testid="wedding-block-phone"
              className="mt-6 inline-block font-serif text-3xl font-semibold tracking-tight text-plum transition-colors duration-300 hover:text-berry"
            >
              {SITE.phone}
            </a>
            <div className="mt-7">
              <motion.a
                href={SITE.phoneHref}
                data-testid="wedding-consultation-button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 400, damping: 22 }}
                className="inline-flex items-center gap-2.5 rounded-full bg-plum px-7 py-3.5 text-center text-[12px] font-bold uppercase leading-snug tracking-[0.12em] text-ivory shadow-lg shadow-plum/25 transition-colors duration-300 hover:bg-berry sm:text-sm"
              >
                <Phone className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
                Call to Schedule a Consultation
              </motion.a>
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand title="Holiday flowers? Order early by phone" />
    </>
  );
}
